import { useState } from "react";
import { Eye, EyeOff, LockKeyhole, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

import SEO from "@/components/site/SEO";
import lmsApi from "@/lib/lmsApi";

function destinationForUser(user) {
  if (user.require_password_change) {
    return "/learn/change-password";
  }

  if (user.is_platform_admin) {
    return "/learn/admin";
  }

  const organisationMembership = user.memberships?.find((item) =>
    ["organisation_admin", "instructor", "auditor"].includes(item.role)
  );

  if (organisationMembership) {
    return `/learn/organisations/${organisationMembership.organisation_id}`;
  }

  return "/learn";
}

export default function LMSLogin() {
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSubmitting(true);
    setError("");

    try {
      const response = await lmsApi.post("/auth/login", {
        username,
        password,
      });

      navigate(destinationForUser(response.data), {
        replace: true,
      });
    } catch (err) {
      const detail = err.response?.data?.detail;

      setError(
        detail ||
          "We could not sign you in. Check your details and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="TRAC-Learn Login | UKTDA"
        description="Secure login to the TRAC-Learn learning management system."
        path="/learn/login"
      />

      <section className="min-h-[calc(100vh-5rem)] bg-slate-50 py-12 md:py-20">
        <div className="container-rail">
          <div className="mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-xl border border-slate-200 bg-white p-2">
                <img
                  src="/TRAC-Learn.svg"
                  alt="TRAC-Learn"
                  className="max-h-full max-w-full"
                />
              </div>

              <div>
                <p className="label-eyebrow text-safety">
                  Secure TRAC-Learn access
                </p>

                <h1 className="mt-1 font-display text-3xl font-bold text-navy">
                  Sign in
                </h1>
              </div>
            </div>

            <div className="mt-6 flex gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-safety" />

              <p>
                Use the username and password issued by UKTDA, your school,
                employer or training administrator.
              </p>
            </div>

            <form
              className="mt-7 space-y-5"
              onSubmit={handleSubmit}
            >
              <div>
                <label
                  htmlFor="lms-username"
                  className="text-sm font-semibold text-navy"
                >
                  Username
                </label>

                <input
                  id="lms-username"
                  name="username"
                  autoComplete="username"
                  value={username}
                  onChange={(event) =>
                    setUsername(event.target.value)
                  }
                  required
                  className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-safety focus:ring-2 focus:ring-safety/20"
                />
              </div>

              <div>
                <label
                  htmlFor="lms-password"
                  className="text-sm font-semibold text-navy"
                >
                  Password
                </label>

                <div className="relative mt-2">
                  <input
                    id="lms-password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) =>
                      setPassword(event.target.value)
                    }
                    required
                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 pr-12 text-slate-900 outline-none transition focus:border-safety focus:ring-2 focus:ring-safety/20"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword((value) => !value)
                    }
                    className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-slate-500 hover:text-navy"
                    aria-label={
                      showPassword
                        ? "Hide password"
                        : "Show password"
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-navy px-5 py-3.5 font-bold text-white transition hover:bg-navy/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <LockKeyhole className="h-5 w-5" />

                {submitting
                  ? "Signing in…"
                  : "Sign in securely"}
              </button>
            </form>

            <p className="mt-6 text-center text-xs leading-relaxed text-slate-500">
              Access is restricted to authorised TRAC-Learn users. Account
              activity may be recorded for security, safeguarding and training
              assurance purposes.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}